import api from '@/api';
import { getClientActiveAid } from '@trove-shared/workspace/activeAid';
import type { Session, SessionsResponse } from '@/types/sessions.types';

/** Returned by the extension SW when embedded web aid ≠ extension activeAid. */
export const WEB_EXTENSION_ACCOUNT_MISMATCH = 'WEB_EXTENSION_ACCOUNT_MISMATCH';

export type GetAllSessionsPayload = {
  action?: string;
  callbackId?: number;
  csid?: string;
  activeSessions?: string[];
  sessions?: Record<string, Session>;
  status?: string;
  code?: string;
  message?: string;
  webAid?: string;
  extensionAid?: string;
  data?: GetAllSessionsPayload;
};

export type SendCBMessage = (
  action: string,
  data: Record<string, unknown>,
  callback: (data: unknown) => void
) => void;

export function parseGetAllSessionsPayload(raw: unknown): GetAllSessionsPayload {
  const rawObj = raw as GetAllSessionsPayload;
  return rawObj?.data ?? rawObj;
}

export function isWebExtensionAccountMismatch(payload: {
  code?: string;
}): boolean {
  return payload?.code === WEB_EXTENSION_ACCOUNT_MISMATCH;
}

export function extensionSessionsToList(
  sessions: Record<string, Session> | undefined
): Session[] {
  if (!sessions || typeof sessions !== 'object') return [];
  return Object.values(sessions)
    .filter((s): s is Session => !!s)
    .map((s) => ({
      ...s,
      icon: s.icon || '',
      isWorkSpace: s.isWorkSpace === true,
      tabId: s.tabId || [],
      tabOrgId: s.tabOrgId || [],
    }));
}

export async function fetchWorkspaceSessionsFromApi(): Promise<Session[]> {
  const response = await api.get<SessionsResponse>('/sessions');
  if (response.data?.status !== 'success' || !Array.isArray(response.data.sessions)) {
    return [];
  }
  return response.data.sessions.map((s) => ({ ...s, isWorkSpace: true }));
}

/** Workspace aid for the embedded web app (localStorage trove_active_aid). */
export function resolveWebAidForExtension(): string | undefined {
  const aid = getClientActiveAid();
  return aid ?? undefined;
}

export type ExtensionSessionsLoadResult = {
  /** Merged unsaved + workspace sessions (extension IDB or API fallback). */
  sessions: Session[];
  csid?: string;
  activeSessions?: string[];
  /** True when web aid ≠ extension aid — sessions came from GET /sessions only. */
  accountMismatch: boolean;
};

/**
 * Load sessions via extension getAllSessions (passes web aid for SW verification).
 * On account mismatch, falls back to GET /sessions for the web workspace only.
 */
export async function loadSessionsViaExtensionOrApi(
  sendCBMessage: SendCBMessage,
  webAid?: string
): Promise<ExtensionSessionsLoadResult> {
  const aid = webAid ?? resolveWebAidForExtension();

  return new Promise<ExtensionSessionsLoadResult>((resolve, reject) => {
    sendCBMessage('getAllSessions', aid ? { aid } : {}, async (rawUnknown: unknown) => {
      try {
        const data = parseGetAllSessionsPayload(rawUnknown);
        if (isWebExtensionAccountMismatch(data)) {
          const workspaceSessions = await fetchWorkspaceSessionsFromApi();
          resolve({ sessions: workspaceSessions, accountMismatch: true });
          return;
        }
        const extensionSessions = extensionSessionsToList(data.sessions);
        const unsavedSessions = extensionSessions.filter((s) => s.isWorkSpace === false);
        let workspaceSessions = extensionSessions.filter((s) => s.isWorkSpace === true);
        // After login / aid flip, SW IDB for the new workspace can still be empty
        // until hydrate catches up — fall back to GET /sessions so reload isn't blank.
        if (workspaceSessions.length === 0) {
          try {
            const fromApi = await fetchWorkspaceSessionsFromApi();
            if (fromApi.length > 0) {
              workspaceSessions = fromApi;
            }
          } catch {
            // Keep extension result (empty) if API is unreachable.
          }
        }
        resolve({
          sessions: [...unsavedSessions, ...workspaceSessions],
          csid: data.csid,
          activeSessions: Array.isArray(data.activeSessions)
            ? data.activeSessions.filter((id): id is string => typeof id === 'string' && id !== '')
            : data.csid
              ? [data.csid]
              : [],
          accountMismatch: false,
        });
      } catch (err) {
        reject(err);
      }
    });
  });
}
